import React, { useState } from 'react';
import {
    Box, Typography, Card, CardContent, IconButton, Avatar, Chip,
    Divider, Button, Grid, useTheme, useMediaQuery, Snackbar, Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Edit as EditIcon,
    Business as BusinessIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    Schedule as ScheduleIcon,
    Photo as PhotoIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import theme from '../../../theme';
import { getLocalizedField } from '../../../utils/localizedFields';
import {
    TitleScreen,
    PrimaryButton,
    SecondaryButton,
    DialogTitleText,
    DialogSectionLabel,
    DialogSectionValue
} from '../../../SystemDesign/styles';
import SalonManagement from './salonManagement';
import FullScreenDialog from '../../../SystemDesign/FullScreenDialog';
import AddSalon from './addSalon';

const SalonDetailCard = ({ salon, onEdit }) => {
    const { t, i18n } = useTranslation();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

    if (!salon) return null;

    const getLocalizedName = (salon) => {
        return getLocalizedField(salon, 'name', i18n.language);
    };

    const getLocalizedDescription = (salon) => {
        return getLocalizedField(salon, 'description', i18n.language);
    };

    const formatOpeningHours = (openingHours) => {
        if (!openingHours) return t('pro_dashboard.salon.not_defined');

        const days = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
        const dayLabels = [t('weekdays.monday_short'), t('weekdays.tuesday_short'), t('weekdays.wednesday_short'), t('weekdays.thursday_short'), t('weekdays.friday_short'), t('weekdays.saturday_short'), t('weekdays.sunday_short')];

        return days.map((day, index) => {
            const hours = openingHours[day];
            if (!hours || hours.ouverture === 'fermé') {
                return `${dayLabels[index]}: ${t('pro_dashboard.common.closed')}`;
            }
            return `${dayLabels[index]}: ${hours.ouverture} - ${hours.fermeture}`;
        }).join('\n');
    };

    const getPrimaryPhoto = () => {
        if (!salon.photos) return null;

        if (Array.isArray(salon.photos)) {
            const primaryPhoto = salon.photos.find(photo => photo.is_primary);
            return primaryPhoto?.url || salon.photos[0]?.url || null;
        }

        if (typeof salon.photos === 'object') {
            const photoUrls = Object.values(salon.photos);
            return photoUrls[0] || null;
        }

        return null;
    };

    const primaryPhotoUrl = getPrimaryPhoto();

    return (
        <Card sx={{
            width: '100%',
            position: isMobile ? 'static' : 'sticky',
            top: isMobile ? 'auto' : 20,
            height: 'fit-content',
            maxHeight: isMobile ? 'none' : 'calc(100vh - 40px)',
            overflow: 'auto',
            borderRadius: theme.borderRadius['2xl'],
            boxShadow: `0 20px 40px ${theme.colors.primary[100]}`,
            border: `2px solid ${theme.colors.primary[100]}`,
            background: primaryPhotoUrl
                ? `url(${primaryPhotoUrl})`
                : `linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.secondary[50]} 100%)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transition: 'all 0.3s ease',
            mb: isMobile ? 3 : 0,
            '&:hover': {
                transform: isMobile ? 'none' : 'translateY(-4px)',
                boxShadow: `0 25px 50px ${theme.colors.primary[200]}`,
                border: `2px solid ${theme.colors.primary[200]}`,
            }
        }}>
            <CardContent sx={{ p: 0 }}>
                <Box sx={{
                    position: 'relative',
                    height: isMobile ? 150 : 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: isMobile ? 2 : 3,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 100%)',
                    borderRadius: `${theme.borderRadius['2xl']} ${theme.borderRadius['2xl']} 0 0`,
                }}>
                    <Typography
                        variant={isMobile ? "h5" : "h4"}
                        sx={{
                            color: theme.colors.background.primary,
                            fontWeight: 'bold',
                            textAlign: 'center',
                            fontSize: isMobile ? '1.5rem' : '2rem',
                            zIndex: 1
                        }}
                    >
                        {getLocalizedName(salon) || t('pro_dashboard.salon.new_salon')}
                    </Typography>
                    <IconButton
                        onClick={onEdit}
                        sx={{
                            position: 'absolute',
                            top: isMobile ? 10 : 20,
                            right: isMobile ? 10 : 20,
                            backgroundColor: theme.colors.background.primary,
                            color: theme.colors.primary[600],
                            zIndex: 1,
                            boxShadow: `0 4px 12px ${theme.colors.primary[200]}`,
                            minWidth: isMobile ? 44 : 48,
                            minHeight: isMobile ? 44 : 48,
                            '&:hover': {
                                backgroundColor: theme.colors.primary[50],
                                color: theme.colors.primary[700],
                                transform: isMobile ? 'scale(1.05)' : 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                </Box>
                <Box sx={{ p: isMobile ? 2 : 4, backgroundColor: theme.colors.background.primary }}>
                    {getLocalizedDescription(salon) && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.colors.primary[600],
                                textAlign: 'center',
                                mb: 3,
                                fontStyle: 'italic',
                                lineHeight: 1.6
                            }}
                        >
                            {getLocalizedDescription(salon)}
                        </Typography>
                    )}
                    <Divider sx={{
                        my: 3,
                        borderColor: theme.colors.primary[200],
                        borderWidth: 1
                    }} />
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                ...DialogTitleText(theme.colors),
                                fontSize: '1.1rem',
                                mb: 2,
                                color: theme.colors.primary[700],
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <BusinessIcon sx={{ color: theme.colors.primary[500] }} />
                            {t('pro_dashboard.salon.contact_info')}
                        </Typography>
                        {salon.formatted_address && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 2,
                                p: isMobile ? 1.5 : 2,
                                backgroundColor: theme.colors.secondary[60],
                                borderRadius: theme.borderRadius.lg,
                                border: `1px solid ${theme.colors.secondary[200]}`
                            }}>
                                <LocationIcon sx={{
                                    ...DialogSectionLabel(theme.colors),
                                    color: theme.colors.primary[500],
                                    mt: 0.2,
                                    mr: 1
                                }} />
                                <Typography sx={{
                                    ...DialogSectionValue(theme.colors),
                                    flex: 1,
                                    lineHeight: 1.5
                                }}>
                                    {salon.formatted_address}
                                </Typography>
                            </Box>
                        )}
                        {salon.phone && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                                p: isMobile ? 1.5 : 2,
                                backgroundColor: theme.colors.secondary[60],
                                borderRadius: theme.borderRadius.lg,
                                border: `1px solid ${theme.colors.secondary[200]}`
                            }}>
                                <PhoneIcon sx={{
                                    ...DialogSectionLabel(theme.colors),
                                    color: theme.colors.primary[500],
                                    mr: 1
                                }} />
                                <Typography sx={{
                                    ...DialogSectionValue(theme.colors),
                                    fontWeight: 'medium'
                                }}>
                                    {salon.phone}
                                </Typography>
                            </Box>
                        )}
                        {salon.email && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                                p: isMobile ? 1.5 : 2,
                                backgroundColor: theme.colors.secondary[60],
                                borderRadius: theme.borderRadius.lg,
                                border: `1px solid ${theme.colors.secondary[200]}`
                            }}>
                                <EmailIcon sx={{
                                    ...DialogSectionLabel(theme.colors),
                                    color: theme.colors.primary[500],
                                    mr: 1
                                }} />
                                <Typography sx={{
                                    ...DialogSectionValue(theme.colors),
                                    fontWeight: 'medium'
                                }}>
                                    {salon.email}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {salon.opening_hours && (
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    ...DialogTitleText(theme.colors),
                                    fontSize: '1.1rem',
                                    mb: 2,
                                    color: theme.colors.primary[700],
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <ScheduleIcon sx={{ color: theme.colors.primary[500] }} />
                                {t('pro_dashboard.salon.opening_hours')}
                            </Typography>
                            <Box sx={{
                                p: isMobile ? 1.5 : 2,
                                backgroundColor: theme.colors.secondary[60],
                                borderRadius: theme.borderRadius.lg,
                                border: `1px solid ${theme.colors.secondary[200]}`
                            }}>
                                <Typography sx={{
                                    ...DialogSectionValue(theme.colors),
                                    whiteSpace: 'pre-line',
                                    fontSize: isMobile ? '0.8rem' : '0.85rem',
                                    lineHeight: 1.8
                                }}>
                                    {formatOpeningHours(salon.opening_hours)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Chip
                            label={t('pro_dashboard.salon.created_success')}
                            sx={{
                                backgroundColor: theme.colors.success[100],
                                color: theme.colors.success[700],
                                border: `1px solid ${theme.colors.success[300]}`,
                                fontWeight: 'bold',
                                fontSize: isMobile ? '0.8rem' : '0.9rem',
                                px: isMobile ? 1 : 2,
                                py: 1,
                                '& .MuiChip-label': {
                                    px: isMobile ? 1 : 2
                                }
                            }}
                        />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const CreationEditView = ({
    open = false,
    salon,
    onClose,
    onEdit,
    isUpdateMode = false,
    salonToUpdate = null,
    onSuccess,
    showOnlyCard = false
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

    const [showEditDialog, setShowEditDialog] = useState(false);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const handleEditClick = () => {
        if (onEdit) {
            onEdit(); // Call the parent's onEdit function
        } else {
            setShowEditDialog(true);
        }
    };

    const handleEditDialogClose = () => {
        setShowEditDialog(false);
    };

    const handleEditSuccess = (result, error) => {
        if (result && !error) {
            setShowEditDialog(false);
            setNotification({
                open: true,
                message: t('pro_dashboard.salon.update_success'),
                severity: 'success',
            });
            if (onSuccess) {
                onSuccess(result);
            }
        } else if (error) {
            setNotification({
                open: true,
                message: t('pro_dashboard.salon.update_error'),
                severity: 'error',
            });
        }
    };

    const handleCloseNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    // Early return for showOnlyCard mode
    if (showOnlyCard && salon) {
        return (
            <>
                <SalonDetailCard
                    salon={salon}
                    onEdit={onEdit}
                />
                <AddSalon
                    open={showEditDialog}
                    onClose={handleEditDialogClose}
                    onSuccess={handleEditSuccess}
                    isUpdateMode={true}
                    salonToUpdate={salon}
                />
            </>
        );
    }

    const content = (
        <>
            <Box sx={{
                minHeight: '100vh',
                backgroundColor: theme.colors.background.secondary,
                p: isMobile ? 1 : 1,
                pt: isMobile ? 1 : 1
            }}>
                <Grid container spacing={isMobile ? 1 : 3} sx={{ width: '100%' }}>
                    {/* Left Side - Salon Management Card */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{
                            width: '100%',
                            backgroundColor: theme.colors.background.primary,
                            borderRadius: theme.borderRadius.xl,
                            boxShadow: `0 8px 25px ${theme.colors.primary[50]}`,
                            border: `1px solid ${theme.colors.primary[100]}`,
                            overflow: 'hidden'
                        }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{
                                    p: isMobile ? 2 : 4,
                                    background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%)`,
                                    color: theme.colors.background.primary
                                }}>
                                    <Typography
                                        variant={isMobile ? "h5" : "h4"}
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: isMobile ? 1 : 2,
                                            fontSize: isMobile ? '1.5rem' : '2rem',
                                            flexDirection: isSmallMobile ? 'column' : 'row',
                                            textAlign: isSmallMobile ? 'center' : 'left'
                                        }}
                                    >
                                        <BusinessIcon sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
                                        {t('pro_dashboard.salon.management_title')}
                                    </Typography>
                                    <Typography
                                        variant={isMobile ? "body2" : "body1"}
                                        sx={{
                                            opacity: 0.9,
                                            textAlign: isSmallMobile ? 'center' : 'left',
                                            fontSize: isMobile ? '0.9rem' : '1rem'
                                        }}
                                    >
                                        {t('pro_dashboard.salon.management_subtitle')}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: isMobile ? 2 : 4 }}>
                                    {!salon && (
                                        <>
                                            <Box sx={{ mb: 2 }}>
                                                {/* <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={onEdit}
                                    sx={{ mr: 2 }}
                                >
                                    CREATION DU SALON
                                </Button> */}
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={onEdit}
                                                    sx={{ mr: 2 }}
                                                >
                                                    {t('pro_dashboard.salon.categories_services')}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={onEdit}
                                                >
                                                    {t('pro_dashboard.salon.employees_planning_btn')}
                                                </Button>
                                            </Box>
                                            <Typography variant="body2" sx={{ color: theme.colors.primary[600], mb: 2 }}>
                                                {t('pro_dashboard.salon.creation')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: theme.colors.primary[600] }}>
                                                {t('pro_dashboard.salon.creation_desc')}
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                onClick={() => {
                                                    if (onClose) onClose();
                                                    navigate('/pro/salon');
                                                }}
                                                sx={{ mt: 2 }}
                                            >
                                                {t('pro_dashboard.salon.quit')}
                                            </Button>
                                        </>
                                    )}
                                    {salon && (
                                        <SalonManagement
                                            isUpdateMode={true}
                                            salonToUpdate={salon}
                                            onSuccess={onSuccess}
                                            onClose={onClose}
                                        />
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Side - Salon Detail Card */}
                    {salon && (
                        <Grid item xs={12} md={4}>
                            <SalonDetailCard
                                salon={salon}
                                onEdit={handleEditClick}
                            />
                        </Grid>
                    )}
                </Grid>

                {/* Edit Salon Dialog */}
                <AddSalon
                    open={showEditDialog}
                    onClose={handleEditDialogClose}
                    onSuccess={handleEditSuccess}
                    isUpdateMode={isUpdateMode}
                    salonToUpdate={salon}
                />
            </Box>

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );

    if (open) {
        return (
            <FullScreenDialog
                open={open}
                triggerButton={<div style={{ display: 'none' }}></div>}
                title={t('pro_dashboard.salon.management_title')}
                subtitle={t('pro_dashboard.salon.manage_subtitle', { name: getLocalizedField(salon, 'name', i18n.language) || 'Salon' })}
                content={content}
                existAction={false}
                onClose={onClose}
                onExternalClose={onClose}
                closeIcon={<CloseIcon />}
                sx={{ backgroundColor: theme.colors.secondary[50] }}
            />
        );
    }

    return content;
};

export default CreationEditView;
